import clsx from "clsx"
import {GatsbyImage, IGatsbyImageData} from "gatsby-plugin-image"
import React, {useContext, useEffect, useRef, useState} from "react"
import {useInView} from "react-hook-inview"
import RichTextField from "~/components/UI/RichText/RichText"
import NavBarContext from "~/contexts/NavBarContext"
import PlayIcon from "~/icons/PlayIcon"
import {VideoSource} from "~/types/VideoSource"

import "./VideoPlayer.css"

interface VideoPlayerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
    title: string | undefined | null
    description: string | undefined | null
    backgroundImage: IGatsbyImageData | undefined | null
    backgroundAlt: string | undefined | null
    videoSources: Array<VideoSource> | VideoSource
}

const VideoPlayer: React.FC<VideoPlayerProps> = props => {
    const {className, title, description, backgroundImage, backgroundAlt, videoSources, children, ...otherProps} = props

    const [playing, setPlaying] = useState(false)
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const [videoPlayerRef, isVideoVisible] = useInView({threshold: 1})
    const navBarContext = useContext(NavBarContext)

    useEffect(() => {
        if (!playing || !videoRef.current) return
        if (!isVideoVisible) {
            videoRef.current.scrollIntoView({
                block: "start",
                behavior: "smooth",
            })
        }
    }, [playing])

    const playAndPause = () => {
        if (!videoRef.current) return
        if (!playing) {
            setPlaying(true)
            videoRef.current.play()
        } else {
            setPlaying(false)
            videoRef.current.pause()
        }
        navBarContext.hide(1000)
    }

    return (
        <div
            className={clsx("full-video-player-m9ce", playing ? "playing-m9ce" : "")}
            {...otherProps}
            ref={videoPlayerRef}
        >
            {backgroundImage && backgroundAlt && (
                <GatsbyImage alt={backgroundAlt} image={backgroundImage} className="image-m9ce" />
            )}

            <div className="overlay-m9ce">
                <button className="play-button-m9ce" onClick={e => playAndPause()}>
                    <PlayIcon className="play-icon-m9ce" />
                </button>
                <h2 className="title-m9ce">{title}</h2>
                <RichTextField className="description-m9ce">{description}</RichTextField>
            </div>
            <video
                playsInline
                className="video-m9ce"
                ref={videoRef}
                onClick={() => playAndPause()}
                onEnded={() => setPlaying(false)}
            >
                {[videoSources].flat().map((source, index) => {
                    if (!source.src || !source.mimeType) return
                    return <source src={source.src} type={source.mimeType} key={index} />
                })}
            </video>
        </div>
    )
}

export default VideoPlayer
